/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

import { fromJS, Map } from 'immutable';
import { createSelector } from 'reselect';
import { getSelectedEntity, getSelectedSubEntityId } from '../selectors';
import { selectFormInitialValues, getCurrentFormValueByFieldName } from '../../form/selectors';
import { onIntegrationListenerFormSubmit, onFormSubmit } from '../';
import { renameObjectKey } from '../../../../utils';
import { getIntegrationProperties } from './utilities';

export const getIntegrations = (state) => state.getIn(['Entities', 'integrations', 'data']);

export const selectIntegrations = createSelector(getIntegrations, (integrations) => {
  return integrations !== undefined
    ? integrations.toJS().map((integration) => ({
      value: integration.name,
      label: integration.name,
    }))
    : undefined;
});

export const selectIntegrationsFormInitialValues = (state) => {
  const initialProperties = getIntegrationProperties('rest');
  //redundunt code
  // if (getSelectedEntity(state) === undefined) {
  //   return fromJS({ active: true, type: 'rest', authType: 'basic', properties: initialProperties });
  // } 
  const initialValues = selectFormInitialValues(state);
  const integrationType = getCurrentFormValueByFieldName(state, 'type');

  if (initialValues && initialValues.get('type') === 'monet') {
    return initialValues.setIn(
      ['properties', 'usernameAsAgentId'],
      initialValues.getIn(['properties', 'usernameAsAgentId']) === 'true'
    );
  }

  if (initialValues && (initialValues.get('type') === 'socialconnector' || integrationType == 'socialconnector')) {
    const rawProperties = initialValues.get('properties');

    const initialSocialConnectorProperties = rawProperties
      ? Object.entries(rawProperties.toJS()).map(([label, value]) => ({ label, value }))
      : [];
    return initialValues.setIn(['socialConnectorProperties'], fromJS(initialSocialConnectorProperties));
  }

  if (initialValues && initialValues.get('type') === 'rest' && getSelectedEntity(state) === undefined) {
    return initialValues.set('authType', 'basic');
  }

  if (initialValues && initialValues.get('type') === 'rest') {
    const backendAuthType = initialValues.get('authType') || initialValues.getIn(['properties', 'authType']);
    if (backendAuthType === 'basic' || backendAuthType === 'token' || backendAuthType === 'noAuth') {
      return initialValues.set('authType', backendAuthType);
    }

    // Legacy fallback for payloads that do not include authType.
    const properties = initialValues.get('properties');
    if (properties && properties.get('token') === '') {
      if (properties.get('username') === '') {
        return initialValues.set('authType', 'noAuth');
      } else {
        return initialValues.set('authType', 'basic');
      }
    } else {
      return initialValues.set('authType', 'token');
    }
  }

  return initialValues;
};

export const getTwilioRegions = () => [
  { awsId: 'default', twilioId: 'default', display: 'Use Tenant Default' },
  { awsId: null, twilioId: 'gll', display: 'Global Low Latency' },
  { awsId: 'ap-southeast-2', twilioId: 'au1', display: 'Australia' },
  { awsId: 'sa-east-1', twilioId: 'br1', display: 'Brazil' },
  { awsId: 'eu-central-1', twilioId: 'de1', display: 'Germany' },
  { awsId: 'us-west-1', twilioId: 'ie1', display: 'Ireland' },
  { awsId: 'ap-northeast-1', twilioId: 'jp1', display: 'Japan' },
  { awsId: 'ap-southeast-1', twilioId: 'sg1', display: 'Singapore' },
  { awsId: 'us-east-1', twilioId: 'us1', display: 'US East Coast (Virginia)' },
  { awsId: null, twilioId: 'au1-ix', display: 'Interconnect - Australia (Sydney)' },
  { awsId: null, twilioId: 'de1-ix', display: 'Interconnect - Germany (Frankfurt)' },
  { awsId: 'eu-west-1', twilioId: 'ie1-tnx', display: 'Interconnect - Ireland' },
  { awsId: null, twilioId: 'jp1-ix', display: 'Interconnect - Japan (Tokyo)' },
  { awsId: null, twilioId: 'sg1-ix', display: 'Interconnect - Singapore' },
  { awsId: null, twilioId: 'us1-ix', display: 'Interconnect - US East Coast (Virginia)' },
  { awsId: null, twilioId: 'us2-ix', display: 'Interconnect - US West Coast (Oregon)' },
];

export const selectTwilioRegions = (state) =>
  getTwilioRegions()
    .map((region) => ({ value: region.twilioId, label: region.display }))
    .filter((region) => region.value !== 'default');

export const selectIntegrationListeners = (state) =>
  getSelectedEntity(state) && getSelectedEntity(state).get('listeners')
    ? getSelectedEntity(state)
      .get('listeners')
      .toJS()
    : [];

export const subEntityFormSubmission = (values, dispatch, props) => {
  const { integrationType, initialValues } = props;
  if (integrationType === 'twilio') {
    const initialKey = initialValues.get('key');
    const initialValue = initialValues.get('value');
    const key = values.get('key');
    const value = values.get('value');
    const twilioFormValues = initialValues.get('initialTwilioFormValues');
    const existingGlobalDialParams =
      (initialValues.get('globalParamsProperties') && initialValues.get('globalParamsProperties').toJS()) || {};
    let newGlobalDialParam = {};
    let newState;
    if (twilioFormValues && existingGlobalDialParams && (!initialKey && !initialValue)) {
      // create global param property
      newGlobalDialParam[key] = value;
      newState = twilioFormValues.setIn(['properties', 'globalDialParams'], {
        ...existingGlobalDialParams,
        ...newGlobalDialParam,
      });
    } else {
      // update global param property
      if (existingGlobalDialParams.hasOwnProperty(initialKey)) {
        existingGlobalDialParams[initialKey] = value;
        const updatedObject = renameObjectKey(existingGlobalDialParams, initialKey, key);
        newState = twilioFormValues.setIn(['properties', 'globalDialParams'], updatedObject);
      }
    }
    // Since twilio Global Params does not have an endpoint to create and update, we handle the whole integration request here
    // So we dispatch onFormSubmit instead of onIntegrationListenerFormSubmit to update the whole integration
    return dispatch(onFormSubmit(newState, props));
  } else if (integrationType === 'ecg') {
    const initialKey = initialValues.get('key');
    const initialValue = initialValues.get('value');
    const key = values.get('key');
    const value = values.get('value');
    const ecgFormValues = initialValues.get('initialEcgFormValues');
    const existingGlobalDialParams =
      (initialValues.get('globalParamsProperties') && initialValues.get('globalParamsProperties').toJS()) || {};
    let newGlobalDialParam = {};
    let newState;
    if (ecgFormValues && existingGlobalDialParams && (!initialKey && !initialValue)) {
      // create global param property
      newGlobalDialParam[key] = value;
      newState = ecgFormValues.setIn(['properties', 'globalDialParams'], {
        ...existingGlobalDialParams,
        ...newGlobalDialParam,
      });
    } else {
      // update global param property
      if (existingGlobalDialParams.hasOwnProperty(initialKey)) {
        existingGlobalDialParams[initialKey] = value;
        const updatedObject = renameObjectKey(existingGlobalDialParams, initialKey, key);
        newState = ecgFormValues.setIn(['properties', 'globalDialParams'], updatedObject);
      }
    }
    return dispatch(onFormSubmit(newState, props));
  } else if (integrationType === 'salesforce') {
    let newState, omniChannelPropertiesRequest;
    if (values.get('listenerType') === 'omnichannel') {
      // filters out email to case properties
      const { properties: { topic, type, ...omniChannelProperties } } = values.toJS();
      omniChannelProperties.routingTopics[0].channelType = 'work-item';
      omniChannelPropertiesRequest = Object.assign({}, omniChannelProperties);
      newState = values.set('properties', omniChannelPropertiesRequest);
    } else {
      // filters out omnichannel properties
      const { properties: { routingTopics, agentWorkTopic, ...emailToCaseProperties } } = values.toJS();
      newState = values.set('properties', emailToCaseProperties).delete('listenerType');
    }
    return dispatch(onIntegrationListenerFormSubmit(newState, props));
  } else {
    return dispatch(onIntegrationListenerFormSubmit(values, props));
  }
};

export const selectIntegrationListenerFormInitialValues = (state) => {
  const selectedSubEntityId = getSelectedSubEntityId(state);
  const integrationType = getCurrentFormValueByFieldName(state, 'type');
  if (integrationType === 'twilio') {
    const globalDialParamFound = selectGlobalDialParams(state).find(
      (globalDialParam) => globalDialParam.key === selectedSubEntityId
    );
    return new Map({
      initialTwilioFormValues: getSelectedEntity(state),
      key: selectedSubEntityId === 'twilioGlobalDialParams' ? '' : globalDialParamFound.key,
      value: selectedSubEntityId === 'twilioGlobalDialParams' ? '' : globalDialParamFound.value,
      globalParamsProperties:
        getSelectedEntity(state).get('properties') &&
        getSelectedEntity(state).getIn(['properties', 'globalDialParams']) &&
        getSelectedEntity(state).getIn(['properties', 'globalDialParams']),
    });
  } else if (integrationType === 'ecg') {
    const globalDialParamFound = selectGlobalDialParams(state).find(
      (globalDialParam) => globalDialParam.key === selectedSubEntityId
    );
    return new Map({
      initialEcgFormValues: getSelectedEntity(state),
      key: selectedSubEntityId === 'ecgGlobalDialParams' ? '' : globalDialParamFound.key,
      value: selectedSubEntityId === 'ecgGlobalDialParams' ? '' : globalDialParamFound.value,
      globalParamsProperties:
        getSelectedEntity(state).get('properties') &&
        getSelectedEntity(state).getIn(['properties', 'globalDialParams']) &&
        getSelectedEntity(state).getIn(['properties', 'globalDialParams']),
    });
  } else {
    return selectedSubEntityId === 'listeners'
      ? new Map({
        name: '',
        active: false,
        ...(integrationType === 'salesforce' && {
          properties: new Map({
            type: 'Case',
          }),
        }),
      })
      : getSelectedEntity(state).get('listeners') &&
      getSelectedEntity(state)
        .get('listeners')
        .find((listener) => listener.get('id') === selectedSubEntityId);
  }
};

// Select Twilio global dial params
export const selectGlobalDialParams = (state) => {
  const globalDialParams =
    getSelectedEntity(state) && getSelectedEntity(state).getIn(['properties', 'globalDialParams'])
      ? getSelectedEntity(state)
        .getIn(['properties', 'globalDialParams'])
        .toJS()
      : [];
  return Object.entries(globalDialParams).map(([key, value]) => ({ key, value }));
};

export const isIntegrationsFetched = (state) => state.getIn(['Entities', 'integrations', 'data']).size === 0;

export const isTwilioWebRtcEnabled = createSelector(
  [getIntegrations],
  (integrations) =>
    integrations && integrations.size > 0
      ? integrations.find(
      (integration) =>
        integration.get('type') === 'twilio' &&
        integration.get('active') &&
        integration.getIn(['properties', 'webRtc'])
    ) !== undefined
      : undefined
);

export const isEcgWebRtcEnabled = createSelector(
  [getIntegrations],
  (integrations) =>
    integrations && integrations.size > 0
      ? integrations.find(
      (integration) =>
        integration.get('type') === 'ecg' &&
        integration.get('active') &&
        integration.getIn(['properties', 'webRtc'])
    ) !== undefined
      : undefined
);

export const isMsteamsExtensionEnabled = createSelector(
  [getIntegrations],
  (integrations) =>
    integrations && integrations.size > 0
      ? integrations.find(
      (integration) =>
        integration.get('type') === 'ms-teams' &&
        integration.get('active') &&
        integration.getIn(['properties', 'extensions'])
    ) !== undefined
      : undefined
);

export const getSelectedIntegrationType = (state) => getSelectedEntity(state) && getSelectedEntity(state).get('type');

export const getGoogleOauthClientId = createSelector(
  [getIntegrations],
  (integrations) => {
    if (integrations && integrations.size > 0) {
      return integrations.filter(integration => integration.get('type') === 'email').toJS()[0].googleOauthClientId;
    } else {
      return undefined;
    }
  }
);

export const getGoogleOauthClientSecret = createSelector(
  [getIntegrations],
  (integrations) => {
    if (integrations && integrations.size > 0) {
      return integrations.filter(integration => integration.get('type') === 'email').toJS()[0].googleOauthClientSecret;
    } else {
      return undefined;
    }
  }
);

export const getMsOauthClientId = createSelector(
  [getIntegrations],
  (integrations) => {
    if (integrations && integrations.size > 0) {
      return integrations.filter(integration => integration.get('type') === 'email').toJS()[0].msOauthClientId;
    } else {
      return undefined;
    }
  }
);

export const getMsOauthClientSecret = createSelector(
  [getIntegrations],
  (integrations) => {
    if (integrations && integrations.size > 0) {
      return integrations.filter(integration => integration.get('type') === 'email').toJS()[0].msOauthClientSecret;
    } else {
      return undefined;
    }
  }
);

export const getOauthAccessTokenResponse = createSelector(
  [getIntegrations],
  (integrations) => {
    if (integrations && integrations.size > 0) {
      return integrations.filter(integration => integration.get('type') === 'email').toJS()[0].oauthAccessTokenResponse;
    } else {
      return undefined;
    }
  }
);

export const getMsOauthPreferredUsername = createSelector(
  [getIntegrations],
  (integrations) => {
    if (integrations && integrations.size > 0) {
      return integrations.filter(integration => integration.get('type') === 'email').toJS()[0].preferred_username;
    } else {
      return undefined;
    }
  }
);

export const getMsOauthPreferredUsernameListener = (state) => {
  const integrations = getIntegrations(state).toJS();
  const selectedSubEntityId = getSelectedSubEntityId(state);
  if (integrations && integrations.length > 0) {
    const newListener = integrations.filter(integration => integration.type === 'email')[0].newListener;
    const listeners = integrations.filter(integration => integration.type === 'email')[0].listeners;
    // extra check to see if listener is present..
    var listener ;
    if(listeners){
      listener = listeners.find(listener => listener.id === selectedSubEntityId);
    } else {
      listener = undefined;
    }

    if (newListener) {
      return newListener.preferred_username;
    } else if (listener && listener.preferred_username) {
      return listener.preferred_username;
    } else {
      return undefined;
    }
  } else {
    return undefined;
  }
};

export const getOptionsDropDown = (msTeamsUpdateAccess, salesForceServiceCloudUpdateAccess, integrations) => {
  const optionsArray = [
    { label: 'REST', value: 'rest' },
    { label: 'Salesforce', value: 'salesforce' },
    { label: 'Zendesk', value: 'zendesk' },
    { label: 'MS Teams', value: 'ms-teams' },
    { label: 'MS Teams Presence', value: 'msteams-presence' },
    { label: 'Salesforce Service Cloud', value: 'salesforce-service-cloud' },
    { label: 'Active Directory', value: 'activedir' },
    { label: 'Social Connector', value: 'socialconnector' },
  ];

  // Check if there's already a ecg integration
  const hasEcgIntegration = integrations.some(integration => integration.get('type') === 'ecg');

  // Add ECG option only if there's no ecg integration yet
  if (!hasEcgIntegration) {
    optionsArray.push({ label: 'ECG', value: 'ecg' });
  }

  return optionsArray.filter((items) => {
    if (msTeamsUpdateAccess !== false && items.value === 'ms-teams') {
      return items
    }
    else if (salesForceServiceCloudUpdateAccess !== false && items.value === 'salesforce-service-cloud') {
      return items
    }
    else if (items.value !== 'ms-teams' && items.value !== 'salesforce-service-cloud') {
      return items;
    }
  });
}
